import { type Component, ComponentType } from "@/types";
import { BaseCommandHandler, type CommandResult } from "../BaseCommandHandler";

interface VotingCardProps extends Record<string, unknown> {
  resolution?: { type?: string; timeoutAction?: string };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isVotingCardProps(value: Record<string, unknown>): value is VotingCardProps {
  const resolution = value.resolution;
  if (resolution === undefined) return true;
  if (!isRecord(resolution)) return false;
  const type = resolution.type;
  const timeoutAction = resolution.timeoutAction;
  return (
    (type === undefined || typeof type === 'string') &&
    (timeoutAction === undefined || typeof timeoutAction === 'string')
  );
}

/**
 * Command Handler for VotingCard components.
 *
 * Handles commands for:
 * - Adding/removing vote options
 * - Changing vote type (approve-veto, single-choice, ranked, thumbs)
 * - Setting resolution mode (unanimous, majority, quorum, any)
 * - Setting deadline
 * - Configuring display settings (show results, voter names, layout)
 */
export class VotingCardCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Pattern: add option
    this.commandPatterns.set(
      /add (?:a )?(?:new )?option\s+(?:called |named |")?(.+?)(?:"|$)/i,
      (match, component) => {
        const label = match[1].trim();
        const existingOptions = Array.isArray(component.properties?.options) ? component.properties.options : [];
        return {
          properties: {
            options: [
              ...existingOptions,
              { id: `opt-${Date.now()}`, label, description: '' }
            ]
          }
        };
      }
    );

    // Pattern: remove option
    this.commandPatterns.set(
      /remove (?:the )?option\s+(?:called |named |")?(.+?)(?:"|$)/i,
      (match, component) => {
        const label = match[1].trim().toLowerCase();
        const options = (Array.isArray(component.properties?.options) ? component.properties.options : [])
          .filter((o: { label: string }) => o.label.toLowerCase() !== label);
        return { properties: { options } };
      }
    );

    // Pattern: set vote type
    this.commandPatterns.set(
      /(?:set |change |switch )?(?:vote |voting )?type (?:to )?(approve.?veto|single.?choice|ranked|thumbs)/i,
      (match) => {
        const typeMap: Record<string, string> = {
          'approve-veto': 'approve-veto', 'approveveto': 'approve-veto',
          'single-choice': 'single-choice', 'singlechoice': 'single-choice',
          'ranked': 'ranked', 'thumbs': 'thumbs',
        };
        const voteType = typeMap[match[1].replace(/\s+/g, '').toLowerCase()] || 'approve-veto';
        return { properties: { voteType } };
      }
    );

    // Pattern: set resolution
    this.commandPatterns.set(
      /(?:set |change )?resolution (?:to )?(unanimous|majority|quorum|any)/i,
      (match, component) => {
        const props = this.typedProperties(component, isVotingCardProps);
        return {
          properties: {
            resolution: {
              ...props?.resolution,
              type: match[1].toLowerCase(),
              timeoutAction: props?.resolution?.timeoutAction ?? 'extend',
            }
          }
        };
      }
    );

    // Pattern: set deadline
    this.commandPatterns.set(
      /(?:set |add )?deadline (?:to |for )?(.+)/i,
      (match) => {
        const dateStr = match[1].trim();
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return { properties: { deadline: date.toISOString() } };
      }
    );

    // Pattern: show/hide results
    this.commandPatterns.set(
      /(?:show |display )results?\s*(always|after.?vote|after.?deadline|never)?/i,
      (match) => {
        const modeMap: Record<string, string> = {
          'always': 'always', 'aftervote': 'after-vote', 'after-vote': 'after-vote',
          'afterdeadline': 'after-deadline', 'after-deadline': 'after-deadline',
          'never': 'never',
        };
        const showResults = modeMap[(match[1] || 'always').replace(/\s+/g, '').toLowerCase()] || 'always';
        return { properties: { showResults } };
      }
    );

    // Pattern: set layout
    this.commandPatterns.set(
      /(?:set |change )?(?:option )?layout (?:to )?(list|grid|card)/i,
      (match) => ({ properties: { optionLayout: match[1].toLowerCase() } })
    );

    // Pattern: show/hide voter names
    this.commandPatterns.set(
      /(show|hide) voter names/i,
      (match) => ({ properties: { showVoterNames: match[1].toLowerCase() === 'show' } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.VOTING_CARD) return false;
    const keywords = [
      'option', 'vote', 'voter', 'resolution', 'deadline',
      'results', 'layout', 'approve', 'veto', 'quorum',
      'majority', 'unanimous', 'ranked', 'thumbs', 'poll',
    ];
    return keywords.some(kw => command.toLowerCase().includes(kw));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const changes = this.matchPatterns(command, component);
    if (changes) {
      return {
        success: true,
        command: {
          type: 'property',
          changes,
          confidence: 0.9,
          description: `Updated voting card: ${Object.keys(changes.properties || {}).join(', ')}`,
        },
        suggestions: [],
      };
    }
    return {
      success: false,
      command: { type: 'property', changes: {}, confidence: 0, description: 'Unrecognized voting command' },
      suggestions: [
        'add option "Option Name"',
        'set vote type to approve-veto',
        'set resolution to majority',
        'set deadline to 2026-04-20',
        'show results always',
        'set layout grid',
      ],
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'add option "Team Lunch at Sushi Place"',
      'remove option "Option C"',
      'set vote type to thumbs',
      'set resolution to majority',
      'set deadline to Friday',
      'show results after vote',
      'set layout card',
      'show voter names',
    ];
  }

  async getSuggestions(): Promise<string[]> {
    return [
      'add option', 'set vote type', 'set resolution',
      'set deadline', 'show results', 'set layout',
    ];
  }
}

export const votingCardCommandHandler = new VotingCardCommandHandler();
